import { useState } from 'react';
import clsx from 'clsx';
import { Card, Button, Badge, Avatar, Checkbox, Progress } from '@lib/ui';
import { Steps, Step, TabGroup, TabList, Tab, TabPanels, TabPanel } from '@lib/ui';

// Icons
const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="size-4"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
    />
  </svg>
);

// Sample data
const sources = [
  { id: 1, name: 'Product Vision Document', insights: 8 },
  { id: 2, name: 'Q4 Market Analysis Report', insights: 12 },
  { id: 3, name: 'AI Research Paper: LLMs in Content', insights: 15 },
  { id: 4, name: 'User Feedback Collection', insights: 9 },
];

const personas = [
  { id: 1, name: 'Thought Leader', avatar: 'TL', color: 'bg-primary-500' },
  { id: 2, name: 'Tech Educator', avatar: 'TE', color: 'bg-secondary-500' },
  { id: 3, name: 'Brand Voice', avatar: 'BV', color: 'bg-success-500' },
  { id: 4, name: 'Community Builder', avatar: 'CB', color: 'bg-warning-500' },
];

const channels = [
  { id: 'linkedin', name: 'LinkedIn Post', icon: '💼', maxLength: 3000 },
  { id: 'twitter', name: 'Twitter Thread', icon: '🐦', maxLength: 280 },
  { id: 'blog', name: 'Blog Article', icon: '📝', maxLength: null },
  { id: 'newsletter', name: 'Newsletter', icon: '📧', maxLength: null },
  { id: 'youtube', name: 'YouTube Script', icon: '▶️', maxLength: null },
];

const insights = [
  {
    id: 1,
    text: 'Creators face a structural mismatch between channel demands and human capacity',
    source: 'Product Vision',
    selected: true,
  },
  {
    id: 2,
    text: 'The real burden is repeated translation—sources into opinions, personas into artifacts',
    source: 'Product Vision',
    selected: true,
  },
  {
    id: 3,
    text: 'Persona should be a first-class primitive, not a prompt tweak',
    source: 'Product Vision',
    selected: false,
  },
  {
    id: 4,
    text: 'Market research shows 68% of creators spend more time reformatting than creating',
    source: 'Q4 Analysis',
    selected: false,
  },
  {
    id: 5,
    text: 'AI-assisted content creation can reduce cognitive load by up to 3x',
    source: 'AI Research',
    selected: true,
  },
];

const generatedContent = {
  linkedin: `The modern creator faces an impossible equation: infinite channels demanding unique content, finite human capacity to produce it.

But here's what most people miss—the bottleneck isn't creativity. It's translation.

We spend our best energy translating:
→ Sources into opinions
→ Opinions into personas
→ Personas into channel-specific formats

At HalfNote, we've built a different model. One where personas are first-class primitives, not prompt tweaks. Where sources are living inputs, not one-off references.

The result? Creators spend their energy forming ideas—not repeatedly re-expressing the same insight.

What's your biggest content creation bottleneck?

#ContentCreation #CreatorEconomy #AI`,

  twitter: `🧵 Thread: Why traditional content creation is broken (and what we're doing about it)

1/ The modern creator faces an impossible math problem: infinite channels × finite time = burnout.

But the bottleneck isn't what you think...

2/ It's not about creating MORE content. It's about the cognitive tax of TRANSLATING content.

Sources → Opinions → Personas → Channels

Each step multiplies the work.

3/ What if personas were first-class primitives instead of prompt tweaks?

What if one insight could flow into many expressions automatically?

4/ That's exactly what we're building at HalfNote.

"Ideas should be the scarce input—not time, tone-matching, or distribution-specific rewriting."

5/ The future of content isn't about producing more.

It's about compounding thinking and maintaining coherent presence across every surface.

What's your biggest content creation challenge? 👇`,

  blog: `# Why Traditional Content Creation is Fundamentally Broken

The modern creator faces a structural mismatch that few talk about openly: the explosion of channels demanding tailored output has far outpaced human capacity to consistently express ideas with quality and voice.

## The Hidden Tax of Content Creation

Most discussions about content creation efficiency miss the real bottleneck. It's not creativity—it's translation.

Every piece of content requires multiple transformation steps:
- **Sources into opinions**: Distilling raw information into viewpoints
- **Opinions into personas**: Adapting viewpoints to specific voices
- **Personas into artifacts**: Formatting for channel-specific requirements

Each step multiplies the cognitive load. A single insight might need to become a LinkedIn post, a Twitter thread, a newsletter section, and a blog paragraph—each requiring different formatting, length, and tone adjustments.

## A Different Mental Model

What if we separated **what is being said** from **how it is said**?

This is the core insight behind HalfNote. By treating personas as reusable intellectual lenses (not prompt tweaks) and sources as living inputs (not one-off references), we can fundamentally change the content creation equation.

### Persona as a First-Class Primitive

Instead of manually adjusting tone for each piece of content, imagine defining a persona once and having it act as a consistent filter across all your output. Same message, different expression—automatically.

### Sources as Living Inputs

Rather than copying and pasting from reference documents, imagine your sources being continuously available, their insights extractable and recombineable at will.

## The Compound Effect

When content becomes a transformation pipeline rather than a blank page, something remarkable happens: your thinking compounds.

Each insight you capture can flow into multiple expressions. Each persona you define becomes more refined with use. The cognitive tax decreases while output increases.

## Conclusion

The future of content creation isn't about producing more. It's about reducing the gap between having an idea and expressing it fully across every channel that matters.

*What's your biggest content creation bottleneck? Share in the comments.*`,
};

export default function ContentPipeline() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSources, setSelectedSources] = useState<number[]>([1]);
  const [selectedPersona, setSelectedPersona] = useState<number>(1);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['linkedin']);
  const [selectedInsights, setSelectedInsights] = useState<number[]>([1, 2, 5]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);

  const handleGenerate = () => {
    setIsGenerating(true);
    // Simulate generation
    setTimeout(() => {
      setIsGenerating(false);
      setGenerationComplete(true);
      setCurrentStep(3);
    }, 3000);
  };

  const toggleSource = (id: number) => {
    setSelectedSources((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleChannel = (id: string) => {
    setSelectedChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleInsight = (id: number) => {
    setSelectedInsights((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Content Pipeline</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            One insight, many expressions. Transform your sources through personas into
            channel-ready content.
          </p>
        </div>
      </div>

      {/* Steps */}
      <Card className="p-6">
        <Steps currentStep={currentStep} className="mb-8">
          <Step title="Select Sources" description="Choose your input materials" />
          <Step title="Choose Persona" description="Define the voice" />
          <Step title="Select Channels" description="Pick output formats" />
          <Step title="Review & Generate" description="Create content" />
        </Steps>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Step 1: Select Sources */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Select sources to draw insights from
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-300">
                Sources are sacred and traceable. Choose the materials that will inform your
                content.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {sources.map((source) => (
                  <div
                    key={source.id}
                    onClick={() => toggleSource(source.id)}
                    className={clsx(
                      'p-4 rounded-lg border-2 cursor-pointer transition-all',
                      selectedSources.includes(source.id)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{source.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-dark-300">
                          {source.insights} insights available
                        </p>
                      </div>
                      {selectedSources.includes(source.id) && (
                        <div className="size-6 rounded-full bg-primary-500 flex items-center justify-center">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Insights from selected sources */}
              {selectedSources.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-600">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                    Select insights to include ({selectedInsights.length} selected)
                  </h4>
                  <div className="space-y-2">
                    {insights.map((insight) => (
                      <div
                        key={insight.id}
                        onClick={() => toggleInsight(insight.id)}
                        className={clsx(
                          'p-3 rounded-lg border cursor-pointer transition-all',
                          selectedInsights.includes(insight.id)
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-dark-600'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedInsights.includes(insight.id)}
                            onChange={() => toggleInsight(insight.id)}
                          />
                          <div>
                            <p className="text-sm text-gray-900 dark:text-white">{insight.text}</p>
                            <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                              Source: {insight.source}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Choose Persona */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Choose a persona for your content
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-300">
                Personas are reusable intellectual lenses. They define how your insights will be
                expressed.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {personas.map((persona) => (
                  <div
                    key={persona.id}
                    onClick={() => setSelectedPersona(persona.id)}
                    className={clsx(
                      'p-4 rounded-lg border-2 cursor-pointer transition-all',
                      selectedPersona === persona.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar size="md" className={persona.color} name={persona.avatar} />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {persona.name}
                        </h4>
                      </div>
                      {selectedPersona === persona.id && (
                        <div className="size-6 rounded-full bg-primary-500 flex items-center justify-center">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Select Channels */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Select output channels
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-300">
                One insight, many expressions. Choose where your content will be published.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {channels.map((channel) => (
                  <div
                    key={channel.id}
                    onClick={() => toggleChannel(channel.id)}
                    className={clsx(
                      'p-4 rounded-lg border-2 cursor-pointer transition-all',
                      selectedChannels.includes(channel.id)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-dark-600 hover:border-gray-300 dark:hover:border-dark-500'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{channel.icon}</span>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {channel.name}
                          </h4>
                          {channel.maxLength && (
                            <p className="text-xs text-gray-500 dark:text-dark-400">
                              Max {channel.maxLength} chars
                            </p>
                          )}
                        </div>
                      </div>
                      {selectedChannels.includes(channel.id) && (
                        <div className="size-6 rounded-full bg-primary-500 flex items-center justify-center">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Review & Generate */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {!generationComplete ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Review your content pipeline
                  </h3>

                  {/* Pipeline Summary */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg overflow-x-auto">
                    <div className="text-center flex-shrink-0">
                      <p className="text-xs text-gray-500 dark:text-dark-400 uppercase">Sources</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedSources.length}
                      </p>
                    </div>
                    <ArrowRightIcon />
                    <div className="text-center flex-shrink-0">
                      <p className="text-xs text-gray-500 dark:text-dark-400 uppercase">Insights</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedInsights.length}
                      </p>
                    </div>
                    <ArrowRightIcon />
                    <div className="text-center flex-shrink-0">
                      <p className="text-xs text-gray-500 dark:text-dark-400 uppercase">Persona</p>
                      <Avatar
                        size="sm"
                        className={personas.find((p) => p.id === selectedPersona)?.color}
                        name={personas.find((p) => p.id === selectedPersona)?.avatar}
                      />
                    </div>
                    <ArrowRightIcon />
                    <div className="text-center flex-shrink-0">
                      <p className="text-xs text-gray-500 dark:text-dark-400 uppercase">Channels</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {selectedChannels.length}
                      </p>
                    </div>
                  </div>

                  {isGenerating && (
                    <div className="text-center py-12">
                      <div className="inline-flex items-center gap-3 text-primary-600 dark:text-primary-400">
                        <SparklesIcon />
                        <span className="text-lg font-medium">Generating content...</span>
                      </div>
                      <Progress value={65} className="max-w-md mx-auto mt-4" color="primary" />
                      <p className="text-sm text-gray-500 dark:text-dark-300 mt-2">
                        Transforming {selectedInsights.length} insights through{' '}
                        {personas.find((p) => p.id === selectedPersona)?.name} persona
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Generated Content
                    </h3>
                    <Badge variant="soft" color="success">
                      {selectedChannels.length} pieces generated
                    </Badge>
                  </div>

                  {/* Content Preview Tabs */}
                  <TabGroup defaultValue={selectedChannels[0]}>
                    <TabList>
                      {selectedChannels.map((channelId) => {
                        const channel = channels.find((c) => c.id === channelId);
                        return (
                          <Tab key={channelId} value={channelId}>
                            <span className="mr-2">{channel?.icon}</span>
                            {channel?.name}
                          </Tab>
                        );
                      })}
                    </TabList>
                    <TabPanels className="mt-4">
                      {selectedChannels.map((channelId) => (
                        <TabPanel key={channelId} value={channelId}>
                          <Card className="p-4">
                            <div className="flex justify-end mb-3">
                              <Button variant="soft" className="px-3 py-1.5 text-sm">
                                <CopyIcon />
                                <span className="ml-2">Copy</span>
                              </Button>
                            </div>
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-dark-200 bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
                                {generatedContent[channelId as keyof typeof generatedContent]}
                              </pre>
                            </div>
                          </Card>
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </TabGroup>
                </>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-dark-600">
          <Button
            variant="flat"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0 || isGenerating}
          >
            Previous
          </Button>

          {currentStep < 3 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={
                (currentStep === 0 && selectedSources.length === 0) ||
                (currentStep === 2 && selectedChannels.length === 0)
              }
            >
              Next
              <ArrowRightIcon />
            </Button>
          ) : !generationComplete ? (
            <Button onClick={handleGenerate} disabled={isGenerating}>
              <SparklesIcon />
              <span className="ml-2">{isGenerating ? 'Generating...' : 'Generate Content'}</span>
            </Button>
          ) : (
            <Button
              onClick={() => {
                setCurrentStep(0);
                setGenerationComplete(false);
              }}
            >
              Create New Content
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
